import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { exhaustMap, of, tap } from 'rxjs';
import { LOCAL_STORAGE_NAMES } from '../../helpers/global/global.constant';
import { AVAILABLE_THEMES, THEME_SWITCHING_CLASS } from '../../helpers/ui/ui.constant';
import { Theme } from '../../interfaces/ui/ui.interface';
import { changeTheme, loadPreviewTheme, loadPreviewThemeSuccess } from './theme.actions';

const THEMES: Theme[] = Object.values(AVAILABLE_THEMES);

@Injectable()
export class ThemeEffects {
  constructor(
    private actions$: Actions,
    @Inject(DOCUMENT) private document: Document
  ) {}

  private changeTheme$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(changeTheme),
        tap(({ theme }) => {
          localStorage.setItem(LOCAL_STORAGE_NAMES.THEME, theme);
        })
      ),
    { dispatch: false }
  );

  private applyThemeToBody$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(changeTheme, loadPreviewThemeSuccess),
        tap(({ theme }) => {
          this.swapThemeClass(theme);
        })
      ),
    { dispatch: false }
  );

  private swapThemeClass(theme: Theme): void {
    const root = this.document.documentElement;
    root.classList.add(THEME_SWITCHING_CLASS);
    this.document.body.classList.toggle(
      AVAILABLE_THEMES.DARK_ONLY,
      theme === AVAILABLE_THEMES.DARK_ONLY
    );
    void root.offsetHeight;
    const view = this.document.defaultView;
    if (!view) {
      root.classList.remove(THEME_SWITCHING_CLASS);
      return;
    }
    view.requestAnimationFrame(() => {
      root.classList.remove(THEME_SWITCHING_CLASS);
    });
  }

  private loadPreviewTheme$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPreviewTheme),
      exhaustMap(() => {
        let selectedTheme: Theme = AVAILABLE_THEMES.LIGHT_ONLY;

        const localStorageData = localStorage.getItem(LOCAL_STORAGE_NAMES.THEME);

        if (!localStorageData) {
          localStorage.setItem(LOCAL_STORAGE_NAMES.THEME, selectedTheme);
        } else if (THEMES.includes(localStorageData as Theme)) {
          selectedTheme = localStorageData as Theme;
        }

        return of(
          loadPreviewThemeSuccess({
            theme: selectedTheme,
          })
        );
      })
    )
  );
}
